import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const STROKE = '#0071BD';
const STROKE_WIDTH = 1.75;
const PAUSE_BETWEEN_STROKES = 0.025;

const prefersReducedMotion = () =>
	window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * @param {SVGPathElement} path
 */
const preparePath = (path) => {
	const length = path.getTotalLength();
	path.style.strokeDasharray = `${length}`;
	path.style.strokeDashoffset = `${length}`;
	path.style.stroke = STROKE;
	path.style.strokeWidth = String(STROKE_WIDTH);
	path.style.strokeLinecap = 'round';
	path.style.strokeLinejoin = 'round';
	path.style.fillOpacity = '0';
	path.style.opacity = '1';
	return length;
};

/**
 * Writing order: left → right (then top) by bounding box.
 * @param {SVGPathElement[]} paths
 */
const sortPathsForWriting = (paths) =>
	[...paths].sort((a, b) => {
		const boxA = a.getBBox();
		const boxB = b.getBBox();
		const dx = boxA.x - boxB.x;
		if (Math.abs(dx) > 2) {
			return dx;
		}
		return boxA.y - boxB.y;
	});

/**
 * Duration scales with stroke length — longer curves take more time.
 * @param {number} length
 */
const strokeDuration = (length) => Math.max(0.15, Math.min(0.65, length / 360));

/**
 * @param {HTMLElement} container
 * @param {SVGSVGElement} svg
 */
const animateSignature = (container, svg) => {
	const strokes = [
		...svg.querySelectorAll('.js-about-signature-path, path[fill^="url(#"]'),
	];
	if (!strokes.length) {
		return;
	}

	const underlays = [...svg.querySelectorAll('path[fill="black"]')];
	container.replaceChildren(svg);

	if (prefersReducedMotion()) {
		gsap.set(underlays, { opacity: 0 });
		gsap.set(strokes, { fillOpacity: 1, strokeOpacity: 0 });
		return;
	}

	gsap.set(underlays, { opacity: 0 });
	gsap.set(strokes, { opacity: 1, fillOpacity: 0, strokeOpacity: 1 });

	const ordered = sortPathsForWriting(strokes);
	ordered.forEach(preparePath);

	const tl = gsap.timeline({
		scrollTrigger: {
			trigger: container.closest('.about__author-block') || container,
			start: 'top 82%',
			once: true,
		},
	});

	ordered.forEach((path, index) => {
		const duration = strokeDuration(path.getTotalLength());

		tl.to(
			path,
			{
				strokeDashoffset: 0,
				duration,
				ease: 'power1.inOut',
			},
			index === 0 ? 0 : `+=${PAUSE_BETWEEN_STROKES}`,
		)
			.to(
				path,
				{
					fillOpacity: 1,
					duration: 0.08,
					ease: 'power1.out',
				},
				'-=0.04',
			)
			.to(
				path,
				{
					strokeOpacity: 0,
					duration: 0.06,
				},
				'-=0.02',
			);
	});
};

/**
 * @param {HTMLElement} container
 */
const initSignature = (container) => {
	const svg = container.querySelector('svg');
	if (!svg) {
		console.warn('[about-signature] inline SVG missing');
		return;
	}
	animateSignature(container, svg);
};

export const initAboutSignatureDraw = () => {
	document.querySelectorAll('.js-about-signature').forEach((container) => {
		if (!(container instanceof HTMLElement)) {
			return;
		}
		initSignature(container);
	});
};
